import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as NextArrow } from '../../assets/icons/next-arrow.svg';
import { ReactComponent as PreviousArrow } from '../../assets/icons/prev-arrow.svg';
import SelectField from '../select-field/SelectField';

interface DateStatus {
  date: number;
  backgroundColor: string;
  borderColor: string;
  textColor?: string;
  labelColor?: string;
  tier?: 'zero' | 'low' | 'medium' | 'high';
  [key: string]: any;
}

interface CalendarProperties {
  dateStatusData: DateStatus[];
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  handleMonthOrYearChange: (month: number, year: number) => void;
  monthAndYear: { month: string; year: string };
  setMonthAndYear: React.Dispatch<React.SetStateAction<{ month: string; year: string }>>;
  keyToLookupUnderData?: string | null; // Key: Value to fetch data under each date
  defaultValueToShow?: string | null; // Optional default value to show under each date
  dataLoading: boolean; // Optional loading state for the data
}

/**
 * HeatMap calendar view
 * @param {any} properties Component properties
 * @returns {any} heatmap calendar
 */
function HeatMapCalendar(properties: Readonly<CalendarProperties>) {
  const {
    dateStatusData,
    selectedDate,
    setSelectedDate,
    handleMonthOrYearChange,
    monthAndYear,
    setMonthAndYear,
    keyToLookupUnderData,
    defaultValueToShow = null,
    dataLoading = false,
  } = properties;
  const { t, i18n } = useTranslation('customDate');
  const [currentDate, setCurrentDate] = useState(new Date());

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const MONTHS = [
    t('january', { defaultValue: 'January' }),
    t('february', { defaultValue: 'February' }),
    t('march', { defaultValue: 'March' }),
    t('april', { defaultValue: 'April' }),
    t('may', { defaultValue: 'May' }),
    t('june', { defaultValue: 'June' }),
    t('july', { defaultValue: 'July' }),
    t('august', { defaultValue: 'August' }),
    t('september', { defaultValue: 'September' }),
    t('october', { defaultValue: 'October' }),
    t('november', { defaultValue: 'November' }),
    t('december', { defaultValue: 'December' }),
  ];

  const DAYS = [
    t('sunShort', { defaultValue: 'Sun' }),
    t('monShort', { defaultValue: 'Mon' }),
    t('tueShort', { defaultValue: 'Tue' }),
    t('wedShort', { defaultValue: 'Wed' }),
    t('thuShort', { defaultValue: 'Thu' }),
    t('friShort', { defaultValue: 'Fri' }),
    t('satShort', { defaultValue: 'Sat' }),
  ];

  /**
   * Checks if next month navigation should be disabled
   * @returns {boolean} Whether next month navigation is disabled
   */
  const isNextMonthDisabled = (): boolean => {
    const today = new Date();
    const nowYear = today.getFullYear();
    const nowMonth = today.getMonth();
    return currentYear > nowYear || (currentYear === nowYear && currentMonth >= nowMonth);
  };

  /**
   * Check if a given day in the currently displayed month and year is in the future
   * @param {number} day Day of the month
   * @returns {boolean} True if the date is in the future
   */
  const isFutureDate = (day: number): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(currentYear, currentMonth, day);
    date.setHours(0, 0, 0, 0);
    return date.getTime() > today.getTime();
  };

  /**
   * Change view prev month
   */
  const previousMonth = () => {
    const newDate = new Date(currentYear, currentMonth - 1, 1);
    setCurrentDate(newDate);
    setMonthAndYear({
      month: newDate.getMonth().toString(),
      year: newDate.getFullYear().toString(),
    });
  };

  /**
   * Change view to month
   */
  const nextMonth = () => {
    if (isNextMonthDisabled()) return;
    const newDate = new Date(currentYear, currentMonth + 1, 1);
    setCurrentDate(newDate);
    setMonthAndYear({
      month: newDate.getMonth().toString(),
      year: newDate.getFullYear().toString(),
    });
  };

  /**
   * Function to get days in a single month
   * @param {any} year year
   * @param {any} month month
   * @returns {any} number of days
   */
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  /**
   * Function to get first date of the month
   * @param {any} year year
   * @param {any} month month
   * @returns {any} first date
   */
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  /**
   * Function to generate days to the specific month
   * @returns {any} days array
   */
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let index = 0; index < firstDayOfMonth; index += 1) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day += 1) {
      days.push(day);
    }

    return days;
  };

  /**
   * Get status / color of the data
   * @param {any} day day
   * @returns {any} date colors
   */
  const getDateColors = (day: number) => dateStatusData.find((item) => item?.date === day) || null;

  /**
   * Handle month change
   * @param {any} name month
   * @param {any} value selected value
   */
  const handleMonthChange = (name: string, value: string) => {
    setMonthAndYear((previousValue) => ({
      ...previousValue,
      month: value,
    }));
    setCurrentDate(new Date(currentYear, parseInt(value, 10), 1));
  };

  /**
   * Handle year change
   * @param {any} name year
   * @param {any} value selected value
   */
  const handleYearChange = (name: string, value: string) => {
    const newYear = parseInt(value, 10);
    const today = new Date();
    let newMonth = currentMonth;
    if (newYear === today.getFullYear() && newMonth > today.getMonth()) {
      newMonth = today.getMonth();
    }
    setMonthAndYear((previousValue) => ({
      ...previousValue,
      year: value,
      month: newMonth.toString(),
    }));
    setCurrentDate(new Date(newYear, newMonth, 1));
  };

  /**
   * Format date as "DD Month YYYY"
   * @param {any} date Date object
   * @returns {any} formatted date string
   */
  const formatDate = (date: Date) => {
    const day = date.getDate();
    const month = MONTHS[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  /**
   * Handle day click
   * @param {any} day day
   */
  const handleDayClick = (day: number | null) => {
    if (day && !isFutureDate(day)) {
      const newSelectedDate = new Date(currentYear, currentMonth, day);
      setSelectedDate(newSelectedDate);
    }
  };

  /**
   * Function to return the date is selected date or not
   * @param {any} day day
   * @returns {any} boolean true if same date
   */
  const isSameDate = (day: number): boolean => {
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth &&
      selectedDate.getFullYear() === currentYear
    );
  };

  useEffect(() => {
    if (monthAndYear.month && monthAndYear.year) {
      handleMonthOrYearChange(Number(monthAndYear.month) + 1, Number(monthAndYear.year));
    }
  }, [monthAndYear]);

  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();

  const availableMonthOptions = MONTHS.map((month, index) => ({
    label: month,
    value: index.toString(),
  })).filter((monthOption) => {
    const selectedYear = Number(monthAndYear.year);
    if (selectedYear === todayYear) {
      return Number(monthOption.value) <= todayMonth;
    }
    return true;
  });

  const yearOptions = Array.from({ length: 10 }, (_, index) => {
    const year = todayYear - index;
    return { label: year.toString(), value: year.toString() };
  });

  return (
    <div className="heatMap-calendar-warp">
      <div className="heatMap-calendar-header">
        <h6>{formatDate(selectedDate)}</h6>
        <div className="heatMap-month-year-selection">
          <SelectField
            name="month"
            inputLabel=""
            selectedValue={monthAndYear.month}
            placeholder="Month"
            onChangeValue={handleMonthChange}
            valueRequired
            options={availableMonthOptions}
            disabledAutoSorting
          />
          <SelectField
            name="year"
            inputLabel=""
            selectedValue={monthAndYear.year}
            placeholder="Year"
            onChangeValue={handleYearChange}
            valueRequired
            options={yearOptions}
            disabledAutoSorting
          />
        </div>
      </div>
      <div className="heatMap-calendar-container">
        {dataLoading && (
          <div className="loader-container">
            <div className="lds-dual-ring" />
          </div>
        )}
        <div className="calendar-month-nav">
          <div className="calendar-month-title">
            {MONTHS[currentMonth]} {currentYear}
          </div>

          <div className="calendar-nav-buttons">
            <button
              type="button"
              className="calendar-nav-btn"
              onClick={previousMonth}
              style={{
                transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>
              <PreviousArrow />
            </button>
            <button
              type="button"
              className={`calendar-nav-btn ${isNextMonthDisabled() ? 'disabled' : ''}`}
              onClick={nextMonth}
              disabled={isNextMonthDisabled()}
              style={{
                transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>
              <NextArrow />
            </button>
          </div>
        </div>

        <div className="calendar-days-header">
          {DAYS.map((day) => (
            <div key={day} className="calendar-day-name">
              {day}
            </div>
          ))}
        </div>

        <div className="calendar-grid">
          {generateCalendarDays().map((day, index) => {
            const isFuture = day ? isFutureDate(day) : false;
            const isSelected = !isFuture && isSameDate(day || 0);

            let dateToDisplay = (
              <div
                key={day ? `${currentYear}-${currentMonth}-${day}` : `empty-${index}`}
                className={`calendar-day-container ${isFuture ? 'disabled' : ''}`}>
                <button
                  type="button"
                  className={`calendar-day ${isSelected ? 'selected' : ''} ${!day ? 'invisible' : ''} ${
                    isFuture ? 'disabled' : ''
                  }`}
                  onClick={() => handleDayClick(day)}
                  disabled={!day || isFuture}>
                  {day}
                </button>
                {day && keyToLookupUnderData && !isFuture && (
                  <p className="tier-zero-label">{defaultValueToShow || ''}</p>
                )}
              </div>
            );
            if (day && !isFuture) {
              const foundedDate: any = getDateColors(day);
              if (foundedDate) {
                const tierClass = foundedDate?.tier ? `tier-${foundedDate.tier}` : 'tier-zero';
                dateToDisplay = (
                  <div
                    key={`${currentYear}-${currentMonth}-${day}`}
                    className={`calendar-day-container ${tierClass}`}>
                    <button
                      type="button"
                      className={`calendar-day ${isSelected ? 'selected' : ''} ${tierClass}`}
                      style={
                        {
                          '--date-background-color': foundedDate.backgroundColor,
                          '--date-border-color': foundedDate.borderColor,
                          '--date-text-color': foundedDate.textColor,
                          '--date-label-color': foundedDate.labelColor,
                        } as React.CSSProperties
                      }
                      onClick={() => handleDayClick(day)}
                      disabled={false}>
                      {day}
                    </button>
                    {keyToLookupUnderData && (
                      <p
                        title={foundedDate?.[keyToLookupUnderData] || ''}
                        className={`${tierClass}-label`}>
                        {foundedDate?.[keyToLookupUnderData] || ''}
                      </p>
                    )}
                  </div>
                );
              }
            }
            return dateToDisplay;
          })}
        </div>
      </div>
    </div>
  );
}

export default HeatMapCalendar;
